import { useCallback, useEffect, useRef, useState } from 'react';
import { pedirPurgas, type MisDominios as Datos } from '../../../common/services/purgas.js';
import type { Idioma } from '../../../i18n/index.js';
import { textosPurgas } from '../textosPurgas.js';
import { BOTON_ESTUDIOS, CAMPO_ESTUDIOS } from './ListaEstudios.js';

/**
 * PURGA-ESTUDIOS: la cuenta de editor purga sus estudios y bloquea su dominio. Si la cuenta no tiene
 * dominios (la API responde 404), no se pinta nada: el resto de /mis-estudios sigue igual.
 */
/** `alPurgar`: la página vuelve a pedir sus envíos, que la purga puede haber borrado. */
export function MisDominios({ token, idioma, alPurgar }: { readonly token: string; readonly idioma: Idioma; readonly alPurgar?: () => void }) {
  const t = textosPurgas(idioma);
  const [datos, setDatos] = useState<Datos | null>();
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const [confirmacion, setConfirmacion] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const vigente = useRef(true);

  const refrescar = useCallback(async () => {
    const r = await pedirPurgas<Datos>(token, '/mios');
    if (vigente.current) setDatos(r.ok ? r.datos : null);
  }, [token]);
  useEffect(() => {
    vigente.current = true;
    void refrescar();
    return () => {
      vigente.current = false;
    };
  }, [refrescar]);

  if (!datos) return null;

  const accion = async (peticion: () => Promise<{ ok: boolean; mensaje?: string | null }>, exito: string) => {
    if (ocupado) return;
    setOcupado(true);
    setMensaje('');
    const r = await peticion();
    if (!vigente.current) return;
    setMensaje(r.ok ? exito : (r.mensaje ?? t.error));
    if (r.ok) {
      setMarcados(new Set());
      setConfirmacion('');
      alPurgar?.();
      await refrescar();
    }
    if (vigente.current) setOcupado(false);
  };

  return (
    <section aria-labelledby='mis-dominios' className='mt-6 flex flex-col gap-4'>
      <h2 id='mis-dominios' className='m-0 font-(family-name:--serif) text-2xl font-bold'>
        {t.misTitulo}
      </h2>
      <p className='m-0'>{t.misExplicacion(datos.dominios.join(', '))}</p>
      {mensaje ? <p role='status'>{mensaje}</p> : null}
      {datos.estudios.length === 0 ? (
        <p className='m-0'>{t.misNinguno}</p>
      ) : (
        <form
          className='flex flex-col gap-3'
          onSubmit={e => {
            e.preventDefault();
            void accion(
              () => pedirPurgas<{ estudios: number }>(token, '/mios', 'POST', { slugs: [...marcados], confirmacion }),
              t.purgado(marcados.size),
            );
          }}
        >
          <fieldset disabled={ocupado} className='m-0 flex min-w-0 flex-col gap-3 border-0 p-0'>
            <ul className='m-0 flex list-none flex-col gap-2 p-0'>
              {datos.estudios.map(e => (
                <li key={e.slug} className='min-w-0 rounded-xl border border-(--line-strong) p-3 break-words'>
                  <label className='flex gap-3'>
                    <input
                      type='checkbox'
                      checked={marcados.has(e.slug)}
                      disabled={e.enPortada}
                      onChange={() => {
                        const nuevo = new Set(marcados);
                        if (nuevo.has(e.slug)) nuevo.delete(e.slug);
                        else nuevo.add(e.slug);
                        setMarcados(nuevo);
                      }}
                    />
                    <span>
                      <strong>{e.titulo}</strong> · {e.editor}
                      {e.enPortada ? <span className='block text-sm'>{t.enPortada}</span> : null}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <label htmlFor='mis-confirmacion'>{t.confirmar}</label>
            <input
              className={CAMPO_ESTUDIOS}
              id='mis-confirmacion'
              inputMode='numeric'
              autoComplete='off'
              value={confirmacion}
              onChange={e => {
                setConfirmacion(e.target.value);
              }}
            />
            <button className={BOTON_ESTUDIOS} type='submit' disabled={marcados.size === 0 || confirmacion.trim() !== String(marcados.size)}>
              {t.purgar}
            </button>
          </fieldset>
        </form>
      )}
      <p className='m-0'>{t.misBloquear}</p>
      <div className='flex flex-wrap gap-3'>
        {datos.dominios.map(d =>
          datos.bloqueados.includes(d) ? (
            <span key={d}>
              {d} · {t.misBloqueado}
            </span>
          ) : (
            <button
              key={d}
              type='button'
              className={BOTON_ESTUDIOS}
              disabled={ocupado}
              onClick={() => {
                void accion(() => pedirPurgas<unknown>(token, '/mios/bloquear', 'POST', { dominio: d }), t.guardado);
              }}
            >
              {t.misBloquearBoton(d)}
            </button>
          ),
        )}
      </div>
    </section>
  );
}
